import { DeviceHistory, NodeStatus } from 'components';
import DeviceControls from 'components/DeviceControls/DeviceControls';
import BaseModal from 'components/Modals/BaseModal';
import BaseTable from 'components/Tables/BaseTable';
// deno-lint-ignore import-prefix-missing
import ConfirmModal from 'components/UI/ConfirmPopup';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
// deno-lint-ignore import-prefix-missing
import { USGS_PROVISIONAL_STATEMENT } from 'features/integrations/features/ConnectedDevices/assets/USGSProvisionalStatment';
import DeviceMap from 'features/integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import { getGroupMapBounds } from 'features/integrations/features/ConnectedDevices/features/DeviceMap/utils/DeviceMapUtils';
import { hasDevicePermissionFromDevice } from 'features/integrations/features/ConnectedSystems/utils/devicePermissionUtils';
import {
  openAwareSettings,
  openDeviceSettings,
  openUSGSSettings,
} from 'features/integrations/state/slices/deviceSlice';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/integrations/utils/deviceComparisionUtils';
import {
  defineGroupType,
  isDeviceTypeAllowed,
} from 'features/integrations/utils/deviceGroupRulesUtils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion as BaseAccordion } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
// deno-lint-ignore import-prefix-missing
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { sendGroupCommand } from 'services/devices/commands/commands.service';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from '../../../../store/slices/Widget/widgetSlice.js';
import { resolveGroupDevices } from './groupSettingsModalUtils';

export default function GroupDeviceSettingsModal({ show, onClose, group }) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const deviceSource = connectedDevices;
  const dispatch = useDispatch();
  const perms = usePerms();

  // Resolve the actual device objects for the selected group.
  const groupDevices = useMemo(
    () => resolveGroupDevices(deviceSource, group),
    [deviceSource, group],
  );
  // Resolve the effective group type based on current membership (may differ from stored type).
  const groupType = useMemo(
    () => defineGroupType(group?.devices, deviceSource, group?.type),
    [group?.devices, group?.type, deviceSource],
  );
  // Compute bounds to frame all group devices on the map.
  const mapBounds = useMemo(() => getGroupMapBounds(groupDevices), [groupDevices]);

  const [virtualDevice, setVirtualDevice] = useState(null);
  const [sending, setSending] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [showProvisionalModal, setShowProvisionalModal] = useState(false);
  const lastGroupIdRef = useRef(null);

  // Session-wide USGS provisional acceptance — shared with the map and graph views.
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);

  const hasUSGSDevices = useMemo(
    () => groupDevices.some((d) => d?.system_type === 'USGS'),
    [groupDevices],
  );

  // Auto-show the disclaimer the first time a USGS group is opened in this session.
  // Once accepted (provisionalAccepted = true), the auto-show is suppressed for the
  // rest of the session. The info icon always re-opens it manually.
  useEffect(() => {
    if (!show) {
      setShowProvisionalModal(false);
      return;
    }
    if (hasUSGSDevices && !provisionalAccepted) {
      setShowProvisionalModal(true);
    }
  }, [show, hasUSGSDevices, provisionalAccepted]);

  // Only allow macro controls when the mix is compatible per rules and has a control layout.
  const macroEligible =
    groupType &&
    layout[groupType.toLowerCase()] &&
    groupDevices.length > 0 &&
    groupDevices.every((device) => isDeviceTypeAllowed(device?.device_type, groupType));

  // Build a virtual device for group controls using the most recent data points.
  const virtualDeviceSeed = useMemo(() => {
    if (!macroEligible || groupDevices.length === 0) return null;
    const deviceData = groupDevices
      .filter((device) => !DeviceIsDeviceType(device, 'Stage'))
      .map((device) => device?.data || [])
      .flat()
      .sort((nextEntry, prevEntry) => (prevEntry?.ts ?? 0) - (nextEntry?.ts ?? 0));
    return {
      ...groupDevices[0],
      device_type: groupType,
      data: deviceData,
    };
  }, [groupDevices, macroEligible, groupType]);

  // Keep the virtual device in sync when switching groups.
  useEffect(() => {
    if (!macroEligible) {
      setVirtualDevice(null);
      lastGroupIdRef.current = group?.id ?? null;
      return;
    }

    const groupChanged = lastGroupIdRef.current !== (group?.id ?? null);
    if ((groupChanged || !virtualDevice) && virtualDeviceSeed) {
      setVirtualDevice(virtualDeviceSeed);
      lastGroupIdRef.current = group?.id ?? null;
    }
  }, [group?.id, macroEligible, virtualDevice, virtualDeviceSeed]);

  // Maintain a selected device for per-device history fallback.
  useEffect(() => {
    if (!groupDevices.length) {
      setSelectedDeviceId(null);
      return;
    }
    const hasSelection = groupDevices.some((device) => device.id === selectedDeviceId);
    if (!hasSelection) {
      setSelectedDeviceId(groupDevices[0].id);
    }
  }, [groupDevices, selectedDeviceId]);

  // Selected device drives the per-device history view.
  const selectedDevice = useMemo(
    () => groupDevices.find((device) => device.id === selectedDeviceId),
    [groupDevices, selectedDeviceId],
  );

  // Route to the correct settings modal per device type.
  const onDeviceSettingsOpen = (event, device) => {
    event.stopPropagation();
    if (DeviceIsSystemType(device, 'Flood')) {
      dispatch(openAwareSettings(device.id));
    } else if (device?.system_type === 'USGS') {
      dispatch(openUSGSSettings(device.id));
    } else {
      dispatch(openDeviceSettings(device.id));
    }
  };

  const canEditGroupDevices = useMemo(() => {
    if (!groupDevices.length) return false;
    return hasDevicePermissionFromDevice(perms, groupDevices[0], 'createEdit');
  }, [groupDevices, perms]);

  const onGroupControlEvent = async (eventType, deviceState) => {
    switch (eventType) {
      case 'update':
        setVirtualDevice(deviceState);
        return;
      case 'submit':
        if (!group?.id) return;
        if (!canEditGroupDevices) return;
        try {
          setSending(true);
          const statusValue = Number(deviceState?.status ?? 0);
          const commentsValue = deviceState?.comments ?? '';
          const commandArgs = {};
          const intervalValue = Number(deviceState?.interval);
          if (Number.isFinite(intervalValue)) commandArgs.interval = intervalValue;
          const repeatValue = Number(deviceState?.repeat);
          if (Number.isFinite(repeatValue)) commandArgs.repeat = repeatValue;
          const volumeValue = Number(deviceState?.volume);
          if (Number.isFinite(volumeValue)) commandArgs.volume = volumeValue;

          const response = await sendGroupCommand(
            group.id,
            statusValue,
            commentsValue,
            commandArgs,
          );
          if (response?.status && response.status !== 200) {
            throw new Error(`Send Group Command failed (${response.status})`);
          }
          setSending(false);
        } catch (error) {
          console.error('Error sending group command:', error);
          setSending(false);
        }
        return;
      case 'cancel-audio':
        if (!group?.id) return;
        try {
          await sendGroupCommand(group.id, 7, '');
        } catch (error) {
          console.error('Error sending group command:', error);
        }
        return;
      default:
        return;
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'alias',
        header: 'Device',
        Cell: ({ row }) => (
          <div className="d-flex flex-column">
            <span className="fw-semibold text-break">{row.original.alias}</span>
            <span className="text-secondary text-break">{row.original.name}</span>
          </div>
        ),
      },
      {
        accessorKey: 'node_id',
        header: 'ID',
        Cell: ({ cell }) => <span>{cell.getValue()}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ row }) => <NodeStatus device={row.original} />,
      },
      {
        id: 'settings',
        header: '',
        size: 60,
        minSize: 48,
        maxSize: 80,
        grow: false,
        Cell: ({ row }) => {
          const device = row.original;
          const canEdit = hasDevicePermissionFromDevice(perms, device, 'createEdit');
          if (!canEdit) return null;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-white"
              onClick={(event) => onDeviceSettingsOpen(event, device)}
            >
              <FaCog size={24} />
            </button>
          );
        },
      },
    ],
    [perms],
  );

  return (
    <BaseModal
      show={show}
      handleShow={onClose}
      title={group?.name || 'Group Settings'}
      size="xl"
      contentStyle={{ overflow: 'hidden' }}
    >
      <div className="d-flex flex-column gap-3 min-h-75vh">
        <div className="w-100 overflow-hidden rounded-3" style={{ height: '260px' }}>
          <DeviceMap mode="group" devicesOverride={groupDevices} mapBoundsOverride={mapBounds} />
        </div>
        <div className="d-flex flex-column">
          {macroEligible && canEditGroupDevices && virtualDevice ? (
            <DeviceControls
              device={virtualDevice}
              devices={groupDevices}
              on={onGroupControlEvent}
              sending={sending}
              audioManagerButtonEnabled={false}
            />
          ) : (
            <div className="bg-primary rounded-3 p-3">
              <h5 className="mb-2">Group Controls</h5>
              <p className="m-0 text-secondary">
                {!macroEligible
                  ? 'This group is not eligible for group controls. Use per-device settings instead.'
                  : 'You do not have permission to apply group controls to this group.'}
              </p>
            </div>
          )}
        </div>

        <div className="d-flex flex-column gap-2 flex-grow-1 min-h-0">
          <div className="d-flex align-items-center gap-2">
            <div className="fw-semibold">Devices in Group</div>
            {hasUSGSDevices && (
              <div className="d-flex">
                <IoMdInformationCircleOutline
                  className="rounded-circle bg-primary p-2 cursor-pointer"
                  style={{ height: 35, width: 35 }}
                  onClick={() => setShowProvisionalModal(true)}
                />
                <span className="align-self-center text-white-50 small ps-3">
                  USGS data may be{' '}
                  <span
                    className="text-decoration-underline cursor-pointer text-white"
                    onClick={() => setShowProvisionalModal(true)}
                  >
                    provisional
                  </span>
                  .
                </span>
              </div>
            )}
          </div>
          <div className="flex-grow-1 min-h-0 overflow-auto">
            <div className="d-none d-lg-block">
              <BaseTable
                columns={columns}
                rows={groupDevices}
                enableRowVirtualization={groupDevices.length > 25}
                onRowClick={(device) => setSelectedDeviceId(device.id)}
                selectedRowId={selectedDeviceId}
                customStyles={{
                  cell: { color: '#ffffff', borderBottom: '1px solid #56577A' },
                  header: { backgroundColor: '#2C373B' },
                }}
              />
            </div>
            <div className="d-block d-lg-none">
              <BaseAccordion className="bordered-table-accordion">
                {groupDevices.map((device, index) => {
                  const canEdit = hasDevicePermissionFromDevice(perms, device, 'createEdit');
                  return (
                    <BaseAccordion.Item
                      key={device.id ?? index}
                      eventKey={String(index)}
                      className="bordered-table-accordion-item"
                    >
                      <BaseAccordion.Header>
                        <div
                          className="d-flex w-100 justify-content-between align-items-center gap-2 pe-2"
                          onClick={() => setSelectedDeviceId(device.id)}
                        >
                          <div className="d-flex flex-column">
                            <span className="fw-semibold text-break">{device.alias}</span>
                            <span className="text-secondary text-break">{device.name}</span>
                          </div>
                          <NodeStatus device={device} />
                        </div>
                      </BaseAccordion.Header>
                      <BaseAccordion.Body className="bg-primary-shadow">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div>
                            <span className="fw-semibold">ID:</span> {device.node_id}
                          </div>
                          {canEdit && (
                            <button
                              type="button"
                              className="btn btn-link p-0 text-white"
                              onClick={(event) => onDeviceSettingsOpen(event, device)}
                            >
                              <FaCog size={24} />
                            </button>
                          )}
                        </div>
                      </BaseAccordion.Body>
                    </BaseAccordion.Item>
                  );
                })}
              </BaseAccordion>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-xxl-row gap-3 fs-6">
          {macroEligible && virtualDevice?.data ? (
            <div className="w-100">
              <DeviceHistory
                title="Group Device History"
                data={virtualDevice.data}
                deviceType={virtualDevice.device_type}
              />
            </div>
          ) : (
            selectedDevice?.data && (
              <div className="w-100">
                <DeviceHistory
                  title="Device History"
                  subTitle={selectedDevice.alias || selectedDevice.name}
                  data={selectedDevice.data}
                  deviceType={selectedDevice.device_type}
                />
              </div>
            )
          )}
        </div>
      </div>

      {hasUSGSDevices && (
        <ConfirmModal
          open={showProvisionalModal}
          setOpen={setShowProvisionalModal}
          options={['Continue']}
          callbacks={[
            () => {
              dispatch(setUsgsProvisonalAccepted(true));
              setShowProvisionalModal(false);
            },
          ]}
          message={USGS_PROVISIONAL_STATEMENT}
          style={{ zIndex: 9999 }}
          closeAction={() => {}}
          size="lg"
        />
      )}
    </BaseModal>
  );
}
